import * as combobox from '@zag-js/combobox';
import { useMachine, normalizeProps, type PropTypes } from '@zag-js/react';
import { useId, useCallback, useEffect, useMemo } from 'react';

import type { CascaderOptionWithPath } from '../types';

export interface UseComboboxOptions {
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  disabled?: boolean;
  placeholder?: string;
  positioning?: combobox.PositioningOptions;
  filteredOptions: CascaderOptionWithPath[];
  onHighlightChange?: (highlightedValue: string | null) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onSelectOption?: (option: CascaderOptionWithPath) => void;
}

export interface UseComboboxReturn {
  comboboxApi: ReturnType<
    typeof combobox.connect<PropTypes, CascaderOptionWithPath>
  >;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

export const useCombobox = ({
  inputRef,
  inputValue,
  disabled = false,
  placeholder = 'Select...',
  filteredOptions,
  positioning,
  onHighlightChange,
  onOpenChange,
  onSelectOption,
}: UseComboboxOptions): UseComboboxReturn => {
  const comboboxId = useId();

  // Create combobox collection from filtered options
  const comboboxCollection = useMemo(
    () =>
      combobox.collection({
        items: filteredOptions,
        itemToValue: (item) => item.fullPath.join('/'),
        itemToString: (item) => item.label,
      }),
    [filteredOptions],
  );

  // Combobox state machine
  const comboboxService = useMachine(combobox.machine, {
    id: comboboxId,
    inputValue,
    disabled,
    placeholder,
    openOnClick: true,
    collection: comboboxCollection,
    autoFocus: false,
    loopFocus: false,
    openOnKeyPress: true,
    positioning: positioning || {
      placement: 'bottom',
      strategy: 'fixed',
    },
    onHighlightChange(
      details: combobox.HighlightChangeDetails<CascaderOptionWithPath>,
    ) {
      if (onHighlightChange) {
        if (!details.highlightedItem) {
          onHighlightChange(null);
        } else {
          const { fullPath } = details.highlightedItem;
          onHighlightChange(fullPath.join('/'));
        }
      }
    },
    onOpenChange(details: { open: boolean }) {
      onOpenChange?.(details.open);
    },
    onValueChange(details: {
      items: CascaderOptionWithPath[];
      value: string[];
    }) {
      onSelectOption?.(details.items[0]);
    },
  });

  const comboboxApi = combobox.connect<PropTypes, CascaderOptionWithPath>(
    comboboxService,
    normalizeProps,
  );

  // When our filtered options change, we need to ensure that the highlighted value is valid
  // If it's not, we set the highlighted value to the first option
  useEffect(() => {
    const currentHighlightedValue = comboboxApi.highlightedValue;
    const hasExplicitHighlightedValue = currentHighlightedValue !== null;
    const hasMatchingHighlightedValue =
      filteredOptions.length > 0 &&
      filteredOptions.some(
        (option) => option.fullPath.join('/') === currentHighlightedValue,
      );
    if (
      filteredOptions.length > 0 &&
      hasExplicitHighlightedValue &&
      !hasMatchingHighlightedValue
    ) {
      comboboxApi.setHighlightValue(filteredOptions[0].fullPath.join('/'));
    }
  }, [filteredOptions, comboboxApi]);

  const setIsOpen = useCallback(
    (open: boolean) => {
      comboboxApi.setOpen(open);
    },
    [comboboxApi],
  );

  // Effect to ensure focus stays on input when menu is open
  useEffect(() => {
    if (comboboxApi.open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [comboboxApi.open, inputRef]);

  return {
    comboboxApi,
    isOpen: comboboxApi.open,
    setIsOpen,
  };
};
